import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import {
  buildFullEdiPreview,
  buildFullEdiText,
} from "../data/ediHelpers";

const EdiLineTokens = ({ tokens }) => (
  <>
    {tokens.map((token, idx) => {
      if (token.type === "tag") {
        return (
          <span key={idx} className="edi-token-tag">
            {token.text}
          </span>
        );
      }
      if (token.type === "delimiter") {
        return (
          <span key={idx} className="edi-token-delimiter">
            {token.text}
          </span>
        );
      }
      if (token.type === "empty" || token.type === "empty-field") {
        return token.text ? (
          <span key={idx} className="edi-token-empty-msg">
            {token.text}
          </span>
        ) : null;
      }
      if (token.type === "placeholder") {
        return (
          <span key={idx} className="edi-token-placeholder">
            {token.text}
          </span>
        );
      }
      return (
        <span key={idx} className={`edi-token-value ${token.type}`}>
          {token.text}
        </span>
      );
    })}
  </>
);

const EdiPreviewDrawer = ({
  open,
  onClose,
  loops,
  selectedSegmentId,
  previewMode,
  onPreviewModeChange,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedLoops, setCollapsedLoops] = useState(new Set());
  const [copyStatus, setCopyStatus] = useState("");
  const scrollRef = useRef(null);
  const lineRefs = useRef({});

  const previewGroups = useMemo(
    () => buildFullEdiPreview(loops, previewMode),
    [loops, previewMode]
  );

  const fullText = useMemo(
    () => buildFullEdiText(previewGroups),
    [previewGroups]
  );

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredGroups = useMemo(() => {
    if (!normalizedSearch) return previewGroups;

    return previewGroups
      .map((group) => ({
        ...group,
        segments: group.segments.filter(
          (seg) =>
            seg.tag.toLowerCase().includes(normalizedSearch) ||
            seg.segmentId.toLowerCase().includes(normalizedSearch) ||
            seg.friendlyName.toLowerCase().includes(normalizedSearch) ||
            seg.line.toLowerCase().includes(normalizedSearch)
        ),
      }))
      .filter((group) => group.segments.length > 0);
  }, [previewGroups, normalizedSearch]);

  const allLoopIds = useMemo(
    () => previewGroups.map((g) => g.loopId),
    [previewGroups]
  );

  const allCollapsed =
    allLoopIds.length > 0 && allLoopIds.every((id) => collapsedLoops.has(id));

  useEffect(() => {
    if (!open || !selectedSegmentId) return;

    const node = lineRefs.current[selectedSegmentId];
    if (node && scrollRef.current) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.classList.add("edi-preview-line-highlight");
      const timer = setTimeout(() => {
        node.classList.remove("edi-preview-line-highlight");
      }, 2200);
      return () => clearTimeout(timer);
    }
  }, [open, selectedSegmentId, previewGroups]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopyStatus("Copied");
    } catch {
      setCopyStatus("Copy failed");
    }
    setTimeout(() => setCopyStatus(""), 2000);
  }, [fullText]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([fullText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `x12-278-preview-${previewMode}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [fullText, previewMode]);

  const handleToggleAllLoops = useCallback(() => {
    if (allCollapsed) {
      setCollapsedLoops(new Set());
    } else {
      setCollapsedLoops(new Set(allLoopIds));
    }
  }, [allCollapsed, allLoopIds]);

  const handleToggleLoop = useCallback((loopId) => {
    setCollapsedLoops((prev) => {
      const next = new Set(prev);
      if (next.has(loopId)) next.delete(loopId);
      else next.add(loopId);
      return next;
    });
  }, []);

  let lineNumber = 0;

  return (
    <Offcanvas
      show={open}
      onHide={onClose}
      placement="end"
      className="edi-preview-offcanvas"
      aria-labelledby="edi-preview-drawer-title"
    >
      <Offcanvas.Header closeButton className="edi-preview-drawer-header">
        <div className="edi-preview-header-content">
          <div className="edi-preview-header-top">
            <span className="edi-preview-header-icon" aria-hidden="true">
              <i className="fh_compare_arrows" />
            </span>
            <div className="edi-preview-header-copy">
              <div className="edi-preview-title-row">
                <Offcanvas.Title id="edi-preview-drawer-title">
                  EDI Preview
                </Offcanvas.Title>
                <span className="edi-preview-header-badge">X12 278</span>
              </div>
              <p className="edi-preview-drawer-subtitle">
                Full transaction preview before Prior Authorization submission
              </p>
            </div>
          </div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="edi-preview-offcanvas-body">
        <div className="edi-preview-mode-toggle" role="radiogroup" aria-label="Preview mode">
          <label className={`edi-preview-mode-option ${previewMode === "template" ? "active" : ""}`}>
            <input
              type="radio"
              name="ediPreviewMode"
              value="template"
              checked={previewMode === "template"}
              onChange={() => onPreviewModeChange("template")}
            />
            EDI Template
          </label>
          <label className={`edi-preview-mode-option ${previewMode === "generated" ? "active" : ""}`}>
            <input
              type="radio"
              name="ediPreviewMode"
              value="generated"
              checked={previewMode === "generated"}
              onChange={() => onPreviewModeChange("generated")}
            />
            Generated EDI
          </label>
        </div>

        <div className="edi-preview-toolbar">
          <button type="button" className="btn btn-border-primary btn-sm" onClick={handleCopy}>
            Copy
          </button>
          <button type="button" className="btn btn-border-primary btn-sm" onClick={handleDownload}>
            Download .txt
          </button>
          <button type="button" className="btn btn-border-primary btn-sm" onClick={handleToggleAllLoops}>
            {allCollapsed ? "Expand All" : "Collapse All"}
          </button>
          <div className="edi-preview-search-wrap">
            <input
              type="search"
              className="edi-preview-search"
              placeholder="Search segment…"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              aria-label="Search segment"
            />
          </div>
          {copyStatus && (
            <span className="edi-preview-copy-status" role="status">
              {copyStatus}
            </span>
          )}
        </div>

        <div className="edi-preview-drawer-body" ref={scrollRef}>
          {filteredGroups.length === 0 ? (
            <p className="edi-preview-empty">
              {normalizedSearch
                ? "No segments match your search."
                : "No visible segments to preview."}
            </p>
          ) : (
            filteredGroups.map((group) => {
              const collapsed = collapsedLoops.has(group.loopId);
              return (
                <section key={group.loopId} className="edi-preview-loop-group">
                  <button
                    type="button"
                    className="edi-preview-loop-header"
                    onClick={() => handleToggleLoop(group.loopId)}
                    aria-expanded={!collapsed}
                  >
                    <span className="edi-preview-loop-chevron">{collapsed ? "▸" : "▾"}</span>
                    <span className="edi-preview-loop-name">{group.loopName}</span>
                    <span className="edi-preview-loop-count">
                      {group.segments.length} segment{group.segments.length === 1 ? "" : "s"}
                    </span>
                  </button>

                  {!collapsed && (
                    <div className="edi-preview-lines">
                      {group.segments.map((seg) => {
                        lineNumber += 1;
                        const currentLine = lineNumber;
                        const isSelected = seg.segmentId === selectedSegmentId;

                        return (
                          <div
                            key={`${group.loopId}-${seg.segmentId}-${currentLine}`}
                            ref={(node) => {
                              if (node) lineRefs.current[seg.segmentId] = node;
                            }}
                            className={`edi-preview-line ${isSelected ? "selected" : ""}`}
                            data-segment-id={seg.segmentId}
                          >
                            <span className="edi-preview-line-number">{currentLine}</span>
                            <code className="edi-preview-line-code">
                              <EdiLineTokens tokens={seg.tokens} />
                            </code>
                            <span className="edi-preview-line-meta">
                              {seg.tag} · {seg.friendlyName}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })
          )}
        </div>

        <footer className="edi-preview-drawer-footer">
          <span className="edi-legend-item">
            <span className="edi-legend-swatch placeholder" /> Template placeholder
          </span>
          <span className="edi-legend-item">
            <span className="edi-legend-swatch dynamic" /> Dynamic (FHIR)
          </span>
          <span className="edi-legend-item">
            <span className="edi-legend-swatch static" /> Static
          </span>
          <span className="edi-legend-item">
            <span className="edi-legend-swatch system" /> System
          </span>
        </footer>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default EdiPreviewDrawer;
